/**
 * Format a date string to dd/mm/yyyy format for display
 * @param {string|Date} dateString - The date string in ISO format or Date object
 * @param {Object} options - Optional configuration
 * @param {boolean} options.showOverdueStatus - Whether to show special text for overdue dates
 * @returns {string|JSX.Element} Formatted date string or JSX for special cases
 */
export const formatDateDisplay = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  const { showOverdueStatus = false } = options;
  const date = new Date(dateString);
  
  // Return dd/mm/yyyy format
  if (!showOverdueStatus) {
    return date.toLocaleDateString('en-GB'); // en-GB locale uses dd/mm/yyyy format
  }
  
  // Handle special cases for due dates (today, tomorrow, overdue)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);
  
  // Check if due date is today
  if (dateOnly.getTime() === today.getTime()) {
    return { type: 'today', label: 'Today' };
  }
  
  // Check if due date is tomorrow
  if (dateOnly.getTime() === tomorrow.getTime()) {
    return { type: 'tomorrow', label: 'Tomorrow' };
  }
  
  // Check if overdue
  if (dateOnly < today) {
    return { 
      type: 'overdue', 
      label: `Overdue: ${date.toLocaleDateString('en-GB')}` 
    };
  }
  
  return date.toLocaleDateString('en-GB');
};

/**
 * Format a datetime string to dd/mm/yyyy HH:MM format for display
 * @param {string|Date} dateString - The date string in ISO format or Date object
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeDisplay = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  const dateFormatted = date.toLocaleDateString('en-GB');
  const timeFormatted = date.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
  
  return `${dateFormatted} at ${timeFormatted}`;
};

/**
 * Format a due date with status-aware overdue highlighting
 * @param {string|Date} dueDate - The due date string in ISO format or Date object
 * @param {string} ticketStatus - The status of the ticket ('open', 'in-progress', 'resolved', 'closed')
 * @returns {string|Object} Formatted date string or JSX object for special cases
 */
export const formatDueDateWithStatus = (dueDate, ticketStatus) => {
  if (!dueDate) return '-';
  
  // Only show overdue status for open or in-progress tickets
  const shouldShowOverdueStatus = ticketStatus === 'open' || ticketStatus === 'in-progress';
  return formatDateDisplay(dueDate, { showOverdueStatus: shouldShowOverdueStatus });
};
